import debounce from 'lodash/debounce'
import { useField } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useCallback, useMemo } from 'react'

import { useAsync } from '../../hooks'
import useClearWhenUnmount from '../../hooks/useClearWhenUnmount'
import Select from './Select'

const DEBOUNCE_MS = 500

export default function AsyncSelect({
  asyncSearch = false,
  clearWhenUnmount = false,
  fetchOptions,
  id = undefined,
  initialFetch = true,
  label = undefined,
  labelInValue = false,
  labelPropName = 'name',
  labelCol = undefined,
  wrapperCol = undefined,
  options = undefined,
  startWithFirstValue = false,
  valuePropName = 'id',
  value = undefined,
  filterOption = undefined,
  placeholder = undefined,
  additionalChangeListener = undefined,
  ...rest
}) {
  const [field, , { setValue, setTouched }] = useField(rest)
  const handleBlur = useCallback(() => {
    if (setTouched) setTouched(true)
  }, [setTouched])
  const {
    pending,
    value: fetchedOptions,
    execute,
  } = useAsync(fetchOptions, initialFetch)
  const debouncedExecute = useMemo(
    () => debounce(execute, DEBOUNCE_MS),
    [execute],
  )
  const finalOptions = useMemo(
    () =>
      [
        ...(Array.isArray(options) ? options : []),
        ...(Array.isArray(fetchedOptions) ? fetchedOptions : []),
      ].reduce((acc, option) => {
        if (!acc.find(o => o[valuePropName] === option[valuePropName])) {
          acc.push(option)
        }
        return acc
      }, []),
    [fetchedOptions, options, valuePropName],
  )
  // Formik will consider each key in the value as a different value to be touched
  // when you use labelInValue

  useEffect(() => {
    if (!startWithFirstValue && !value) return
    const hasValue = labelInValue
      ? field.value && field.value.key
      : !!field.value
    if (!pending && !hasValue && finalOptions && finalOptions.length) {
      if (startWithFirstValue) {
        const newValue = labelInValue
          ? {
              value: finalOptions[0][valuePropName],
              label: finalOptions[0][labelPropName],
            }
          : finalOptions[0][valuePropName]
        // Only set if different to prevent infinite loop
        if (JSON.stringify(field.value) !== JSON.stringify(newValue)) {
          setValue(newValue)
        }
      } else {
        const newValue = labelInValue ? { value: value.key } : value
        if (JSON.stringify(field.value) !== JSON.stringify(newValue)) {
          setValue(newValue)
        }
      }
    }
  }, [
    finalOptions,
    labelPropName,
    pending,
    labelInValue,
    startWithFirstValue,
    valuePropName,
    field.value,
    setValue,
    value,
  ])

  useClearWhenUnmount(clearWhenUnmount, setValue, setTouched)

  const onChange = selectValue => {
    setValue(selectValue)
    if (additionalChangeListener) {
      additionalChangeListener(selectValue)
    }
  }

  const defaultFilterOption = useCallback(
    (input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0,
    [],
  )

  return (
    <Select
      id={id}
      loading={pending}
      onChange={onChange}
      onBlur={handleBlur}
      labelInValue={labelInValue}
      value={field.value}
      onSearch={asyncSearch ? debouncedExecute : undefined}
      filterOption={filterOption || defaultFilterOption}
      options={finalOptions}
      labelPropName={labelPropName}
      valuePropName={valuePropName}
      label={label}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      placeholder={placeholder}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  )
}

AsyncSelect.propTypes = {
  asyncSearch: PropTypes.bool,
  containerClassName: PropTypes.string,
  fetchOptions: PropTypes.func.isRequired,
  additionalChangeListener: PropTypes.func,
  id: PropTypes.string,
  initialFetch: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelInValue: PropTypes.bool,
  labelPropName: PropTypes.string,
  labelCol: PropTypes.shape({}),
  wrapperCol: PropTypes.shape({}),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  valuePropName: PropTypes.string,
  startWithFirstValue: PropTypes.bool,
  clearWhenUnmount: PropTypes.bool,
  fullWidth: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  filterOption: PropTypes.func,
  placeholder: PropTypes.string,
}
